import { useAsyncOperation } from "@core/hooks/use-async-operation";
import { analyticsService, libraryFilterManager } from "@core/service-container";
import { Exercise } from "@domain/exercise/exercise";
import {
	AnalyticsViewExerciseDetailOrigin,
	sendAnalyticsEventViewExerciseDetail,
} from "@domain/library/exercise-analytics";
import { DialogContext, ModalId } from "@ui/components/dialog/dialog-provider";
import { TrackingContext } from "@ui/components/common/tracking-context-provider";
import { OnboardingContext } from "@ui/pages/program-builder/onboarding/onboarding-context-provider";
import { useDelayableCallback } from "@utils/hook-utils";
import { useObservable } from "micro-observables";
import { useCallback, useContext, useMemo } from "react";

interface UseExerciseLibraryHook {
	filteredSelectedExercises: Exercise[];
	filteredExercises: Exercise[];
	isAnyFilterActive: boolean;
	hasSelectedExercises: boolean;
	refreshLibraryExercises: () => void;
	isLoading: boolean;
	isError: boolean;
	openExerciseDetailsModal: (exercise: Exercise) => void;
	search: string;
}

export const useExerciseLibrary = (): UseExerciseLibraryHook => {
	const dialogContext = useContext(DialogContext);
	const trackingContext = useContext(TrackingContext);
	useContext(OnboardingContext);
	const search = useObservable(libraryFilterManager.searchWord);
	const selectedExercises = useObservable(libraryFilterManager.selectedExercises);
	const filteredSelectedExercises = useObservable(libraryFilterManager.filteredSelectedExercises);
	const filteredExercises = useObservable(libraryFilterManager.filteredExercises);
	const alreadySelectedExercises = useObservable(libraryFilterManager.exerciseWithAlternativesEditedList);

	const [runRefreshLibraryExercises, isLoading, isError] = useAsyncOperation(
		useCallback(async () => {
			await libraryFilterManager.clearLibraryFiltersAndSearch();
			await libraryFilterManager.listAllExercises();
		}, []),
		{},
	);

	const activeFilters = useObservable(libraryFilterManager.activeCategoryFilters);

	const activeFiltersCount = useMemo(
		() => activeFilters.reduce((acc, category) => acc + category.filters.reduce(accByCat => accByCat + 1, 0), 0),
		[activeFilters],
	);

	const isAnyFilterActive = useMemo(() => activeFiltersCount > 0 || !!search, [activeFiltersCount, search]);

	const hasSelectedExercises = useMemo(() => selectedExercises.length > 0, [selectedExercises]);

	const openExerciseDetailsModal = useCallback(
		(exercise: Exercise) => {
			const exercises = [...filteredSelectedExercises, ...filteredExercises, ...alreadySelectedExercises];
			dialogContext?.openModal(ModalId.ExerciseDetails, {
				relatedExercises: exercises,
				exerciseIndex: exercises.findIndex(ex => ex.draggableId === exercise.draggableId),
				tabHeaderDisabled: true,
				trackingContext,
				editorName: "",
			});
			sendAnalyticsEventViewExerciseDetail(exercise, AnalyticsViewExerciseDetailOrigin.LIBRARY);
		},
		[
			dialogContext,
			filteredSelectedExercises,
			filteredExercises,
			alreadySelectedExercises,
			trackingContext,
		],
	);

	const sendSearchAnalyticsEvent = useCallback(
		(newSearch: string) =>
			analyticsService.trackEvent({
				category: trackingContext.category,
				action: "searchLibrary",
				params: [newSearch],
			}),
		[trackingContext.category],
	);

	const sendEventOnlyIfSearchIsNotEmpty = useCallback((newSearch: string) => !!newSearch, []);

	useDelayableCallback(search, sendSearchAnalyticsEvent, sendEventOnlyIfSearchIsNotEmpty);

	const refreshLibraryExercises = useCallback(() => {
		void runRefreshLibraryExercises();
	}, [runRefreshLibraryExercises]);

	return {
		filteredSelectedExercises: filteredSelectedExercises,
		filteredExercises,
		isAnyFilterActive,
		hasSelectedExercises: hasSelectedExercises,
		refreshLibraryExercises,
		openExerciseDetailsModal,
		isError,
		isLoading,
		search,
	};
};
